import React from "react";
import { Box, Typography, Divider, Button, Card, CardContent, Stack } from "@mui/material";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { TRASPASO_ESTADO } from "../../constants";
import EstadoBadge from "./EstadoBadge";

/**
 * Componente que muestra el detalle completo de un traspaso
 */
export default function DetalleTraspaso({ detalle, onMarcarTraspasado }) {
    const cab = detalle.cabecera ?? detalle;
    const lineas = detalle.lineas ?? detalle.Lineas ?? [];

    // El detalle puede tener el estado en la cabecera o en la raíz del objeto
    // dependiendo del endpoint. Normalizamos para no depender de la estructura exacta.
    const estadoActual = (cab.estado ?? detalle.estado ?? "").toUpperCase();
    const estaTraspasado = estadoActual === TRASPASO_ESTADO.TRASPASADO;

    return (
        <Box>
            {/* Información general */}
            <Box
                sx={{
                    p: 2,
                    backgroundColor: "#f8f9fa",
                    borderRadius: 2,
                    border: "1px solid #e9ecef",
                    mb: 3,
                }}
            >
                <Stack spacing={1.5}>
                    <Row label="ID" value={`#${cab.id ?? cab.ID ?? cab.codigo}`} />
                    <Row label="Fecha" value={cab.fecha ? String(cab.fecha).slice(0, 19).replace("T", " ") : ""} />
                    <Row label="Almacén origen" value={cab.almacenOrigenNombre ?? cab.origen} />
                    <Row label="Almacén destino" value={cab.almacenDestinoNombre ?? cab.destino} />
                    <Row label="Empleado origen" value={cab.empleadoOrigenNombre ?? ""} />
                    <Row label="Empleado destino" value={cab.empleadoDestinoNombre ?? ""} />
                    {(cab.observaciones || cab.comentario) && (
                        <Row label="Observaciones" value={cab.observaciones ?? cab.comentario} />
                    )}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: "#6c757d",
                                minWidth: 110,
                            }}
                        >
                            Estado:
                        </Typography>
                        <EstadoBadge estado={estadoActual} />
                    </Box>
                </Stack>
            </Box>

            {/* Botón marcar traspasado */}
            {!estaTraspasado && onMarcarTraspasado && (
                <Box sx={{ mb: 3 }}>
                    <Button
                        variant="contained"
                        startIcon={<TaskAltIcon />}
                        fullWidth
                        onClick={() => onMarcarTraspasado(detalle)}
                        sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            py: 1.25,
                            fontSize: 15,
                            backgroundColor: "#cc071e",
                            "&:hover": {
                                backgroundColor: "#a30618",
                            },
                        }}
                    >
                        Marcar como traspasado
                    </Button>
                </Box>
            )}

            <Divider sx={{ mb: 3 }} />

            {/* Líneas del traspaso */}
            <Typography
                variant="subtitle1"
                sx={{
                    fontWeight: 700,
                    color: "#212529",
                    mb: 2,
                }}
            >
                Líneas ({lineas.length})
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {lineas.length === 0 ? (
                    <Typography
                        variant="body2"
                        sx={{
                            color: "#6c757d",
                            textAlign: "center",
                            py: 3,
                        }}
                    >
                        Sin líneas
                    </Typography>
                ) : (
                    lineas.map((l, i) => (
                        <Card
                            key={l.id ?? `${l.codigoArticulo}-${i}`}
                            variant="outlined"
                            sx={{
                                borderColor: "#e9ecef",
                                "&:hover": {
                                    borderColor: "#dee2e6",
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                                },
                            }}
                        >
                            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        color: "#212529",
                                        fontSize: 14,
                                        mb: 0.5,
                                    }}
                                >
                                    {l.codigoArticulo ?? l.CODIGO ?? l.ARTICULO}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "#495057",
                                        fontSize: 13,
                                        mb: 1,
                                    }}
                                >
                                    {l.nombre ?? l.NOMBRE ?? "Sin nombre"}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "#6c757d",
                                        fontSize: 12,
                                    }}
                                >
                                    Barras: {l.barras ?? l.BARRAS ?? "-"} · Unidades: {l.unidades ?? l.UNIDADES ?? 0}
                                </Typography>
                            </CardContent>
                        </Card>
                    ))
                )}
            </Box>
        </Box>
    );
}

function Row({ label, value }) {
    return (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
            <Typography
                variant="body2"
                sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#6c757d",
                    minWidth: 110,
                }}
            >
                {label}:
            </Typography>
            <Typography
                variant="body2"
                sx={{
                    textAlign: "right",
                    fontWeight: 500,
                    color: "#212529",
                    fontSize: 14,
                    flex: 1,
                }}
            >
                {value || "-"}
            </Typography>
        </Box>
    );
}
